<script lang="ts">
	import type { Mediator, SvelteAsr, i18n } from 'types/common'
	import type { BaseAttachmentFile } from '@isoftdata/svelte-attachments'
	import type { SalesOrderCustomerMessage, SalesOrderInventoryMessage } from './sales-order'
	import { graphql, type InitialLoadQuery$result, type SalesOrder$result } from '$houdini'
	import {
		buildSalesOrderUpdateInput,
		snapshotSalesOrderBasicFields,
		type SalesOrderBasicFieldsSnapshot,
	} from 'utility/sales-order-update'
	import { formatCustomerCurrency } from 'utility/customer-helper'
	import { getEventValue } from '@isoftdata/browser-event'
	import { formatISO, parseISO } from 'date-fns'
	import { getContext, onMount, tick } from 'svelte'

	import Button from '@isoftdata/svelte-button'
	import DateInput from '@isoftdata/svelte-date-input'
	import Fieldset from '@isoftdata/svelte-fieldset'
	import Input from '@isoftdata/svelte-input'
	import Modal from '@isoftdata/svelte-modal'
	import Select from '@isoftdata/svelte-select'
	import StateCardHeader from 'components/StateCardHeader.svelte'
	import CustomerInfoCard from './CustomerInfoCard.svelte'
	import SalesOrderDetailCard from './SalesOrderDetailCard.svelte'

	const { t: translate } = getContext<i18n>('i18next')

	// Feedbacks (could work on this in the next version):
	// - maybe, add thumbnail image for each line item, if available
	// - have the line items' individual messages in the line item details tab as well
	// - add badges on the tabs for the line items, adjustments, attachments, messages, and payments to indicate the number of items in each tab, or just icons, whichever makes sense
	// - for messages, we need some indicator to show that there are any important messages

	interface Props {
		asr: SvelteAsr
		salesOrderId: number | null
		storeId: number | null
		salesOrder: SalesOrder$result['salesOrder']
		salesPersonList: InitialLoadQuery$result['userAccounts']
		shippingMethods: InitialLoadQuery$result['shippingMethods']
		taxItems: InitialLoadQuery$result['taxItems']
		attachments: Array<BaseAttachmentFile & { salesOrderFileId: number }>
		SODucumnetTypes: InitialLoadQuery$result['salesOrderDocumentTypes']
		salesOrderTerms: InitialLoadQuery$result['salesOrderTerms']
		salesOrderCustomerMessage: SalesOrderCustomerMessage | null
		salesOrderInventoryMessage: SalesOrderInventoryMessage | null
		SOSettingValues: InitialLoadQuery$result['settingValues']['salesOrders']
		editEnabled: boolean
		canChangeDate: boolean
	}

	let {
		asr,
		salesOrderId = null,
		storeId = null,
		salesOrder,
		salesPersonList,
		shippingMethods,
		taxItems,
		attachments,
		SODucumnetTypes,
		salesOrderTerms,
		salesOrderCustomerMessage = null,
		salesOrderInventoryMessage = null,
		SOSettingValues = { showSequentialInvoiceNumbers: false },
		editEnabled = false,
		canChangeDate = false,
	}: Props = $props()

	let isSaving = $state(false)
	let salesOrderChanged = $state(false)
	let viewOnlyMode = $derived(!editEnabled)
	// The Date field needs SALES_ORDERS_CAN_CHANGE_DATE on top of the usual edit permission - the API
	// enforces this server-side, so gate the picker too rather than letting the user edit a field the
	// save would reject.
	let dateEditEnabled = $derived(editEnabled && canChangeDate && !!salesOrder)

	// Snapshot the basic fields as loaded so we only send the ones the user actually changed.
	// This matters for `date`: the API requires the separate SALES_ORDERS_CAN_CHANGE_DATE permission
	// whenever a non-null `date` is supplied, so sending it on every save would break a comment-only
	// save for a user who can edit but cannot change the date. A field left unchanged is omitted, which
	// the API treats identically to sending `null` (ignored, not cleared). Populated in onMount so it
	// captures the loaded values before the user edits them (the screen remounts on every save).
	let originalBasicFields: SalesOrderBasicFieldsSnapshot = {
		internalComments: null,
		externalComments: null,
		date: null,
		truckNumber: null,
		purchaseOrderNumber: null,
	}

	export function canLeaveState(): boolean {
		return !salesOrderChanged
	}

	const SOBillSummary: { label: string; value: string | undefined; bold: boolean }[] = $derived([
		{ label: translate('common:subtotal', 'Subtotal'), value: salesOrder?.subtotal, bold: false },
		{ label: translate('common:tax', 'Tax'), value: salesOrder?.tax, bold: false },
		{ label: translate('common:total', 'Total'), value: salesOrder?.total, bold: true },
		{ label: translate('common:paid', 'Paid'), value: salesOrder?.paid, bold: false },
		{ label: translate('common:balance', 'Balance'), value: salesOrder?.balance, bold: false },
	])

	const mediator = getContext<Mediator>('mediator')
	let showModal = $state(false)
	let isValidatingSalesOrder = $state(false)
	let modalErrorMessage: string | null = $state(null)

	async function openSOLookupModal() {
		showModal = true
		await tick()
		document.getElementById('sales-order-number-input')?.focus()
	}

	async function validateSalesOrder() {
		if (!salesOrderId || !storeId) {
			modalErrorMessage = translate(
				'salesOrder.pleaseEnterAValidSalesOrderNumberAndStoreId',
				'Please enter a valid Sales Order Number and Store ID.',
			)
			return
		}

		isValidatingSalesOrder = true
		try {
			const { data } = await validateSalesOrderQuery.fetch({
				variables: {
					key: {
						storeId,
						salesOrderId,
					},
				},
			})

			if (!data?.salesOrder) {
				throw new Error('Sales Order not found.')
			}
			showModal = false
			modalErrorMessage = null
			// Reload the state to get the sales order data
			asr.go(null, { salesOrderId, storeId }, { inherit: false })
		} catch (error) {
			modalErrorMessage = translate(
				'salesOrder.salesOrderNotFoundInStore',
				'Sales Order {{salesOrderId}} not found in Store {{storeId}}. Please check the Sales Order Number and Store ID.',
				{ salesOrderId, storeId },
			)
		} finally {
			isValidatingSalesOrder = false
		}
	}

	const validateSalesOrderQuery = graphql(`
		query ValidateSalesOrder($key: SalesOrderKey!) {
			salesOrder(key: $key) {
				id
			}
		}
	`)

	async function saveSalesOrder() {
		if (!salesOrder || !storeId || !salesOrderId) {
			return
		}
		isSaving = true
		try {
			// Only send fields the user actually changed - see buildSalesOrderUpdateInput for why
			// `date` in particular must not be sent on every save.
			const input = buildSalesOrderUpdateInput({ storeId, salesOrderId }, originalBasicFields, salesOrder)

			const { data } = await updateSalesOrderMutation.mutate({ input })
			if (!data?.updateSalesOrder) {
				throw new Error(translate('error.noDataReturnedFromTheServer', 'No data returned from the server'))
			}
			mediator.publish('showMessage', {
				type: 'success',
				heading: translate('common:savedHeading', 'Saved!'),
				message: translate('salesOrder.salesOrderHasBeenSavedSuccessfully', 'Sales order has been saved successfully'),
				time: 10,
			})
			salesOrderChanged = false
			asr.go(null, { salesOrderId, storeId, lastResetTime: Date.now() }, { replace: true, inherit: true })
		} catch (error: any) {
			mediator.publish('showMessage', {
				type: 'danger',
				heading: translate('salesOrder.failedToSaveSalesOrder', 'Failed To Save Sales Order'),
				message: error.message,
				time: false,
			})
		} finally {
			isSaving = false
		}
	}

	const updateSalesOrderMutation = graphql(`
		mutation UpdateSalesOrderBasicFields($input: SalesOrderUpdate!) {
			updateSalesOrder(input: $input) {
				id
				date
				externalComments
				internalComments
				purchaseOrderNumber
				truckNumber
			}
		}
	`)

	onMount(() => {
		if (salesOrder) {
			originalBasicFields = snapshotSalesOrderBasicFields(salesOrder)
		}
		if (salesOrder && salesOrderId && storeId) {
			const activityTitle = `#${salesOrder.id} (${salesOrder.documentType.name})`
			mediator.publish('activity', {
				icon: 'dollar',
				type: 'SALES_ORDER',
				route: 'app.sales-order',
				name: activityTitle,
				title: activityTitle,
				parameters: {
					storeId: storeId.toString(),
					salesOrderId: salesOrderId.toString(),
				},
			})
		}
	})
</script>

<div class="card">
	<StateCardHeader
		icon="dollar-sign"
		title="Sales Order"
	>
		{#snippet right()}
			<div
				class="d-flex"
				style="align-items: flex-end"
			>
				{#if editEnabled}
					<Button
						disabled
						outline
						size="sm"
						class="ml-2"
						color="success"
						iconClass="plus"
					>
						{translate('salesOrder.newSale', 'New Sale')}
					</Button>
					<Button
						outline
						class="ml-2"
						size="sm"
						color="primary"
						iconClass="save"
						isLoading={isSaving}
						disabled={isSaving || !salesOrderChanged}
						onclick={saveSalesOrder}
					>
						{translate('salesOrder.saveSalesOrder', 'Save Sales Order')}
					</Button>
				{:else}
					{translate('salesOrder.editingNotCurrentlySupported', 'Editing not currently supported')}.
				{/if}
			</div>
		{/snippet}
	</StateCardHeader>
	<div class="card-body">
		<div class="row">
			<div class="col-12 col-lg-2">
				<Fieldset
					label="Document"
					class="h-100"
				>
					<div class="row">
						<div class="col-12">
							<Input
								readonly
								label={translate('common:orderNum', 'Order #')}
								labelParentClass="mb-0"
								placeholder={translate('salesOrder.newOrder', 'New Order')}
								class="cursor-pointer"
								value={storeId && salesOrderId ? `${storeId}-${salesOrderId}` : ''}
								onclick={async () => {
									await openSOLookupModal()
								}}
							>
								{#snippet append()}
									<Button
										size="sm"
										iconClass="search"
										onclick={async () => {
											await openSOLookupModal()
										}}
									/>
								{/snippet}
							</Input>
						</div>
						<div class="col-12">
							<Select
								disabled
								label={translate('common:documentType', 'Document Type')}
								value={salesOrder?.documentType.id || null}
							>
								{#each SODucumnetTypes as type}
									<option value={type.id}>{type.name}</option>
								{/each}
							</Select>
						</div>
						{#if SOSettingValues.showSequentialInvoiceNumbers}
							<div class="col-12">
								<Input
									readonly
									label={translate('common:invoiceNum', 'Invoice #')}
									value={salesOrder?.sequentialId?.toString() || ''}
								/>
							</div>
						{/if}
						<div class="col-12">
							<DateInput
								disabled={!dateEditEnabled}
								title={editEnabled && !canChangeDate
									? translate(
											'salesOrder.changeDatePermissionRequired',
											'You do not have permission to change the Sales Order date.',
										)
									: undefined}
								label={translate('common:date', 'Date')}
								value={salesOrder?.date ? formatISO(salesOrder.date, { representation: 'date' }) : ''}
								onchange={event => {
									const value = getEventValue(event)
									// Date is NOT NULL - only accept a real date, never a clear-to-empty.
									if (salesOrder && value) {
										salesOrder = { ...salesOrder, date: parseISO(value) }
										salesOrderChanged = true
									}
								}}
							/>
						</div>

						{#if salesOrder?.documentType.canExpire}
							<div class="col-12">
								<Input
									readonly
									type="date"
									label={translate('common:expirationDate', 'Expiration Date')}
									value={salesOrder?.expirationDate
										? formatISO(salesOrder.expirationDate, { representation: 'date' })
										: ''}
								/>
							</div>
						{/if}
					</div>
				</Fieldset>
			</div>
			<div class="col-12 col-md-10">
				<CustomerInfoCard
					{asr}
					bind:salesOrder
					{viewOnlyMode}
					bind:salesOrderChanged
					{salesPersonList}
					{shippingMethods}
					{taxItems}
					selectedCustomerDetailTab="CUSTOMER_DETAIL"
				/>
			</div>
		</div>
		<hr />
		<SalesOrderDetailCard
			{asr}
			{salesOrder}
			{attachments}
			{salesOrderTerms}
			{salesOrderCustomerMessage}
			{salesOrderInventoryMessage}
		/>
		<hr class="mb-1" />
		<div class="row">
			{#each SOBillSummary as item}
				<div class="col-6 col-md-1">
					<Input
						readonly
						label={item.label}
						class="text-right {item.bold ? 'font-weight-bold' : ''}"
						value={item.value ? formatCustomerCurrency(item.value) : '$0.00'}
					/>
				</div>
			{/each}
		</div>
	</div>
</div>

<Modal
	show={showModal}
	confirmButtonType="submit"
	confirmButtonFormId="sales-order-validation-form"
	confirmButtonText={translate('salesOrder.loadSale', 'Load Sale')}
	confirmButtonColor="primary"
	confirmButtonDisabled={!storeId || !salesOrderId}
	confirmButtonIsLoading={isValidatingSalesOrder}
	cancelButtonText={translate('common:cancel', 'Cancel')}
	close={() => (showModal = false)}
	size="sm"
	title={translate('salesOrder.loadSalesOrder', 'Load Sales Order')}
>
	<form
		id="sales-order-validation-form"
		onsubmit={event => {
			event.preventDefault()
			validateSalesOrder()
		}}
	>
		<div class="row">
			<div class="col-4">
				<Input
					label={translate('common:store', 'Store')}
					labelParentClass="mb-0"
					bind:value={storeId}
				/>
			</div>
			<div class="col-8">
				<Input
					id="sales-order-number-input"
					label={translate('salesOrder.salesOrderNumber', 'Sales Order Number')}
					labelParentClass="mb-0"
					value={null}
					onchange={event => {
						salesOrderId = parseInt(getEventValue(event), 10)
					}}
				/>
			</div>
		</div>
	</form>
	{#if modalErrorMessage}
		<div class="alert alert-danger mt-2">
			{modalErrorMessage}
		</div>
	{/if}
</Modal>
